import React, { useState, useMemo, useEffect } from 'react';
import { PositionedGroup } from './useFullReportViewModel';
import { Wrench, FlaskConical, Palette, Users, Briefcase, Calculator, Brain } from 'lucide-react';
import { GroupInfoData } from '../../../data/catData';

type CompassProps = {
  groups: PositionedGroup[];
  groupInfoMap?: Record<string, GroupInfoData>; // Optional GROUP_INFO from API
};

// Map Holland code to group key
const codeToGroupKey: Record<string, string> = {
  R: 'group1',
  I: 'group2',
  A: 'group3',
  S: 'group4',
  E: 'group5',
  C: 'group6',
};

// Standard RIASEC Order for the hexagon
const ORDERED_CODES = ['R', 'I', 'A', 'S', 'E', 'C'];

// Hook to get responsive container size
function useResponsiveSize(maxSize: number = 550, minSize: number = 300) {
  const [size, setSize] = useState(maxSize);

  useEffect(() => {
    const calculateSize = () => {
      if (typeof window === 'undefined') return maxSize;
      // Use viewport width minus padding - increased to 100px to prevent label cutoff
      // Labels extend beyond the chart container, so we need extra space
      const availableWidth = window.innerWidth - 100;
      // Clamp between min and max
      return Math.max(minSize, Math.min(maxSize, availableWidth));
    };

    setSize(calculateSize());

    const handleResize = () => {
      setSize(calculateSize());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [maxSize, minSize]);

  return size;
}

export function Compass({ groups, groupInfoMap = {} }: CompassProps) {
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);

  // Responsive container sizing
  const containerSize = useResponsiveSize(550, 280);
  const center = containerSize / 2;

  // Scale radius proportionally
  const scaleFactor = containerSize / 550;
  const radius = 160 * scaleFactor; // Radius for the icons
  const chartRadius = 110 * scaleFactor; // Radius for the score polygon (max score)

  // Configuration for each Holland Code
  const config: Record<
    string,
    {
      icon: any;
      color: string;
      label: string;
      angle: number; // in degrees, -90 is top
    }
  > = {
    R: { icon: Wrench, color: '#dc2626', label: 'Kỹ thuật', angle: -90 }, // Top
    I: { icon: FlaskConical, color: '#0369A1', label: 'Nghiên cứu', angle: -30 }, // Top Right
    A: { icon: Palette, color: '#F97316', label: 'Nghệ thuật', angle: 30 }, // Bottom Right
    S: { icon: Users, color: '#16a34a', label: 'Xã hội', angle: 90 }, // Bottom
    E: { icon: Briefcase, color: '#9333ea', label: 'Quản lý', angle: 150 }, // Bottom Left
    C: { icon: Calculator, color: '#6b7280', label: 'Nghiệp vụ', angle: 210 }, // Top Left
  };

  // Identify top 3 groups by score DESC, then by group order ASC (tiebreaker)
  const top3Codes = useMemo(() => {
    const sortedGroups = [...groups].sort((a, b) => {
      const scoreA = a.score?.actualScore || 0;
      const scoreB = b.score?.actualScore || 0;

      // First: Sort by score (descending)
      if (scoreB !== scoreA) {
        return scoreB - scoreA;
      }

      // Tiebreaker: Sort by group order (ascending)
      // Lower order numbers get priority when scores are equal
      return (a.order || 0) - (b.order || 0);
    });
    return new Set(sortedGroups.slice(0, 3).map(g => g.code));
  }, [groups]);

  // Helper to get coordinates
  const getCoords = (angleDeg: number, r: number) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: center + r * Math.cos(angleRad),
      y: center + r * Math.sin(angleRad),
    };
  };

  // Generate polygon points for the user's scores
  const scorePolygonPoints = useMemo(() => {
    return ORDERED_CODES.map(code => {
      const group = groups.find(g => g.code === code);
      const score = group?.score?.actualScore || 0;
      const maxScore = group?.score?.maxScore || 8;
      const ratio = Math.min(score / maxScore, 1);

      // Calculate visual radius for score (min 10% to prevent collapsing to center completely)
      const r = chartRadius * ratio;
      const { x, y } = getCoords(config[code].angle, r);
      return `${x},${y}`;
    }).join(' ');
  }, [groups, center, chartRadius]);

  // Generate hexagon background points (Web Grid)
  const getHexagonPoints = (rRatio: number) => {
    return ORDERED_CODES.map(code => {
      const { x, y } = getCoords(config[code].angle, chartRadius * rRatio);
      return `${x},${y}`;
    }).join(' ');
  };

  const hoveredGroup = hoveredCode ? groups.find(g => g.code === hoveredCode) : null;
  const hoveredGroupInfo = hoveredCode ? groupInfoMap[codeToGroupKey[hoveredCode]] : null;

  return (
    <div
      className='relative mx-auto flex items-center justify-center font-sans select-none'
      style={{ width: containerSize, height: containerSize }}
    >
      {/* SVG Layer for Chart */}
      <svg
        width={containerSize}
        height={containerSize}
        className='absolute inset-0 z-0 pointer-events-none'
      >
        {/* Radar Grids (Concentric Hexagons) */}
        {[0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <polygon
            key={i}
            points={getHexagonPoints(ratio)}
            fill='none'
            stroke='#E5E7EB'
            strokeWidth={1}
            strokeDasharray={i === 3 ? '0' : '4 4'}
            className='dark:stroke-gray-700'
          />
        ))}

        {/* Axis Lines with animated pulses for top 3 */}
        {ORDERED_CODES.map((code, idx) => {
          const { x, y } = getCoords(config[code].angle, chartRadius);
          const isTop3 = top3Codes.has(code);

          // Get score position for pulse animation
          const group = groups.find(g => g.code === code);
          const score = group?.score?.actualScore || 0;
          const maxScore = group?.score?.maxScore || 8;
          const ratio = Math.min(score / maxScore, 1);
          const scoreRadius = chartRadius * ratio;
          const { x: scoreX, y: scoreY } = getCoords(config[code].angle, scoreRadius);

          return (
            <g key={code}>
              {/* Axis line */}
              <line
                x1={center}
                y1={center}
                x2={x}
                y2={y}
                stroke='#E5E7EB'
                strokeWidth={1}
                className='dark:stroke-gray-700'
              />

              {/* Animated pulse for top 3 - travels from badge to score dot */}
              {isTop3 && (
                <circle
                  cx={0}
                  cy={0}
                  r={3}
                  fill='#dc2626'
                  className='animate-pulse-travel'
                  style={{
                    animationDelay: `${idx * 0.3}s`,
                  }}
                >
                  <animateMotion
                    dur='2s'
                    repeatCount='indefinite'
                    begin={`${idx * 0.3}s`}
                    path={`M ${x} ${y} L ${scoreX} ${scoreY}`}
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* User Score Polygon */}
        <polygon
          points={scorePolygonPoints}
          fill='rgba(249, 115, 22, 0.2)'
          stroke='#F97316'
          strokeWidth={3}
          strokeLinejoin='round'
          className='drop-shadow-md origin-center animate-grow-polygon'
        />

        {/* Score Points (Dots) */}
        {ORDERED_CODES.map((code, index) => {
          const group = groups.find(g => g.code === code);
          const score = group?.score?.actualScore || 0;
          const maxScore = group?.score?.maxScore || 8;
          const ratio = Math.min(score / maxScore, 1);
          const { x, y } = getCoords(config[code].angle, chartRadius * ratio);
          const isTop3 = top3Codes.has(code);

          return (
            <g key={code}>
              {/* Score dot with shadow */}
              <circle
                cx={x}
                cy={y}
                r={isTop3 ? 6.5 : 4}
                fill={isTop3 ? '#dc2626' : '#F97316'}
                filter={isTop3 ? 'url(#shadow)' : undefined}
                className='origin-center animate-pop-in'
                style={{ animationDelay: `${index * 100 + 500}ms` }}
              />
              {/* White highlight on dot for 3D effect */}
              {isTop3 && (
                <circle
                  cx={x - 1.5}
                  cy={y - 1.5}
                  r={2}
                  fill='white'
                  opacity={0.6}
                  className='animate-pop-in'
                  style={{ animationDelay: `${index * 100 + 550}ms` }}
                />
              )}
            </g>
          );
        })}

        {/* SVG Filters for effects */}
        <defs>
          <filter id='shadow' x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur in='SourceAlpha' stdDeviation='2' />
            <feOffset dx='0' dy='1' result='offsetblur' />
            <feComponentTransfer>
              <feFuncA type='linear' slope='0.5' />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
        </defs>
      </svg>

      {/* Nodes (Icons) */}
      {ORDERED_CODES.map((code, idx) => {
        const conf = config[code];
        const group = groups.find(g => g.code === code);
        const { x, y } = getCoords(conf.angle, radius);
        const isHovered = hoveredCode === code;

        return (
          <div
            key={code}
            className={`absolute flex flex-col items-center justify-center transition-all duration-300 cursor-pointer z-10`}
            style={{
              left: x,
              top: y,
              transform: `translate(-50%, -50%) ${isHovered ? 'scale(1.15)' : 'scale(1)'}`,
            }}
            onMouseEnter={() => setHoveredCode(code)}
            onMouseLeave={() => setHoveredCode(null)}
          >
            {/* Hexagon Shape Background for Icon */}
            <div
              className={`flex items-center justify-center rounded-2xl shadow-sm mb-2 transition-colors duration-300 border-2 ${isHovered
                ? 'bg-white border-transparent shadow-lg dark:bg-gray-800'
                : 'bg-white/90 border-gray-100 dark:bg-gray-800/90 dark:border-gray-700'
                }`}
              style={{
                width: Math.max(32, 48 * scaleFactor),
                height: Math.max(32, 48 * scaleFactor),
                borderColor: isHovered ? conf.color : undefined,
              }}
            >
              <conf.icon
                className='transition-colors font-bold'
                style={{
                  color: conf.color,
                  width: Math.max(16, 24 * scaleFactor),
                  height: Math.max(16, 24 * scaleFactor),
                }}
                strokeWidth={2.5}
              />
            </div>

            {/* Label */}
            <div className='flex flex-col items-center whitespace-nowrap'>
              <span
                className='font-bold uppercase tracking-wider mb-0.5'
                style={{
                  color: conf.color,
                  fontSize: `${Math.max(9, 12 * scaleFactor)}px`,
                }}
              >
                {conf.label}
              </span>
              {top3Codes.has(code) ? (
                <span
                  className='font-extrabold text-white px-2 py-0.5 rounded-full shadow-lg shadow-red-500/50 relative overflow-hidden transition-transform duration-300 hover:scale-110'
                  style={{
                    fontSize: `${Math.max(11, 15 * scaleFactor)}px`,
                    padding: scaleFactor < 0.7 ? '2px 6px' : '4px 10px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)',
                    backgroundSize: '200% 100%',
                  }}
                >
                  <span className='relative z-10'>{group?.score?.actualScore || 0}đ</span>
                  {/* Shimmer effect - slides left to right along the badge with staggered delay */}
                  <span
                    className='absolute inset-0'
                    style={{
                      background:
                        'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                      backgroundSize: '200% 100%',
                      animation: `shimmer 3s linear infinite`,
                      animationDelay: `${idx * 0.5}s`,
                    }}
                  />
                </span>
              ) : (
                <span
                  className='font-bold text-gray-500 dark:text-gray-400 transition-all duration-300'
                  style={{
                    fontSize: '11px',
                  }}
                >
                  {group?.score?.actualScore || 0}đ
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Center Brain/User Icon */}
      <div
        className='absolute z-20 rounded-full bg-white dark:bg-gray-800 shadow-lg border-4 flex items-center justify-center'
        style={{
          left: center - (Math.max(40, 64 * scaleFactor) / 2),
          top: center - (Math.max(40, 64 * scaleFactor) / 2),
          width: Math.max(40, 64 * scaleFactor),
          height: Math.max(40, 64 * scaleFactor),
          borderColor: '#fff'
        }}
      >
        <div className='w-full h-full rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center'>
          <Brain
            className='text-white'
            style={{
              width: Math.max(20, 32 * scaleFactor),
              height: Math.max(20, 32 * scaleFactor)
            }}
          />
        </div>
      </div>

      {/* Hover Information Card */}
      {hoveredGroup && hoveredGroupInfo && (
        <div className='absolute z-30 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-xl p-4 max-w-[260px] border border-gray-100 dark:border-gray-700 bottom-0 left-1/2 -translate-x-1/2 translate-y-full pointer-events-none'>
          <div className='flex items-center gap-2 mb-2 pb-2 border-b border-gray-100 dark:border-gray-700'>
            <div
              className='w-2 h-8 rounded-full'
              style={{ backgroundColor: config[hoveredGroup.code].color }}
            />
            <div>
              <h4 className='font-bold text-gray-900 dark:text-white text-sm leading-tight'>
                {hoveredGroup.name} ({hoveredGroup.code})
              </h4>
              <div className='text-xs text-gray-500'>
                Điểm:{' '}
                <b style={{ color: config[hoveredGroup.code].color }}>
                  {hoveredGroup.score?.actualScore || 0}
                </b>
                /{hoveredGroup.score?.maxScore || 8}
              </div>
            </div>
          </div>
          <p className='text-xs text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed'>
            {hoveredGroup?.shortDesc ||
              hoveredGroupInfo?.shortDesc ||
              'Thông tin chi tiết sẽ được hiển thị sau khi tải.'}
          </p>
        </div>
      )}

      {/* CSS Keyframes */}
      <style>{`
        @keyframes growPolygon {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes popIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes pulseRingOuter {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.4; 
            transform: scale(1.15); 
          }
        }
        @keyframes pulseRingMiddle {
          0%, 100% { 
            opacity: 0.4; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.2); 
          }
        }
        @keyframes glow {
          0%, 100% { 
            opacity: 0.15; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.3; 
            transform: scale(1.1); 
          }
        }
        @keyframes shimmer {
          0% { 
            background-position: -200% 0; 
          }
          100% { 
            background-position: 200% 0; 
          }
        }
        .animate-grow-polygon {
          animation: growPolygon 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                     float 3s ease-in-out infinite 1s;
        }
        .animate-pop-in {
          opacity: 0;
          animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                     float 3s ease-in-out infinite 1s;
        }
        .animate-pulse-ring-outer {
          opacity: 0;
          animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                     pulseRingOuter 3s ease-in-out infinite 1s;
        }
        .animate-pulse-ring-middle {
          opacity: 0;
          animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                     pulseRingMiddle 2.5s ease-in-out infinite 1.2s;
        }
        .animate-glow {
          opacity: 0;
          animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, 
                     glow 2s ease-in-out infinite 1s;
        }
        .animate-shimmer {
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </div>
  );
}
